import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { ApiError, api } from '../../api/index.js';

const KINDS = [
  { value: 'announcement', label: 'Announcement', hint: 'Generic operator broadcast.' },
  { value: 'alert',        label: 'Alert',        hint: 'Operational caution.' },
  { value: 'order',        label: 'Order',        hint: 'Action required.' },
  { value: 'system',       label: 'System',       hint: 'Platform-level notice.' },
];
const PRIORITIES = ['routine', 'priority', 'immediate', 'flash'];

const TARGET_TYPES = [
  { value: 'all',  label: 'All operators',  needs: null,            hint: 'Every active operator account.' },
  { value: 'role', label: 'By role',        needs: 'role',          hint: 'admin or user.' },
  { value: 'user', label: 'Specific user',  needs: 'user',          hint: 'Pick from the operator list.' },
];

export function ComposeNotificationModal({ open, onClose, onSent }) {
  const [values, setValues] = useState(empty());
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setValues(empty());
    setBusy(false);
    setError(null);
    api.users.list().then((r) => setUsers(r.users || [])).catch(() => setUsers([]));
  }, [open]);

  const targetMeta = TARGET_TYPES.find((t) => t.value === values.targetType);
  const errors = useMemo(() => {
    const e = {};
    if (!values.title.trim()) e.title = 'Title is required.';
    if (values.title.length > 200) e.title = 'Max 200 characters.';
    if (values.body && values.body.length > 4096) e.body = 'Max 4096 characters.';
    if (targetMeta?.needs && !values.targetValue) e.targetValue = 'Target is required.';
    if (values.expiresAt) {
      const t = Date.parse(values.expiresAt);
      if (Number.isNaN(t)) e.expiresAt = 'Invalid date.';
      else if (t <= Date.now()) e.expiresAt = 'Must be in the future.';
    }
    return e;
  }, [values, targetMeta]);

  const canSubmit = Object.keys(errors).length === 0 && !busy;

  function update(field, v) { setValues((p) => ({ ...p, [field]: v })); }

  async function submit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        kind: values.kind,
        priority: values.priority,
        title: values.title.trim(),
        body: values.body.trim() || null,
        targetType: values.targetType,
        targetValue: targetMeta?.needs ? values.targetValue : null,
        expiresAt: values.expiresAt ? new Date(values.expiresAt).toISOString() : null,
      };
      await onSent(payload);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Send failed.');
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Send notification"
      description="Operator-side notifications go to active sessions immediately. Terminal-side delivery (units / channels / persons) activates with Stage 2 mesh nodes."
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary" data-skip-autofocus>
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={!canSubmit} className="btn-primary">
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Sending…
              </>
            ) : (
              <>
                <i className="fa-solid fa-paper-plane" aria-hidden="true" /> Send
              </>
            )}
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}

        <div>
          <label className="field-label">Kind</label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {KINDS.map((k) => {
              const active = values.kind === k.value;
              return (
                <label
                  key={k.value}
                  className={
                    'flex cursor-pointer flex-col gap-0.5 rounded-2xl border px-3 py-2.5 transition-colors ' +
                    (active ? 'border-accent bg-accent-soft' : 'border-line bg-white hover:bg-canvas')
                  }
                >
                  <input
                    type="radio"
                    name="n-kind"
                    className="sr-only"
                    value={k.value}
                    checked={active}
                    onChange={() => update('kind', k.value)}
                    disabled={busy}
                  />
                  <span className="text-sm font-semibold text-ink">{k.label}</span>
                  <span className="text-[11px] leading-snug text-mist">{k.hint}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <label htmlFor="n-title" className="field-label">Title</label>
          <input
            id="n-title"
            className="field"
            value={values.title}
            onChange={(e) => update('title', e.target.value)}
            maxLength={200}
            disabled={busy}
            placeholder="One-line headline"
          />
          {errors.title && <p className="mt-1 text-xs text-rose-600">{errors.title}</p>}
        </div>

        <div>
          <label htmlFor="n-body" className="field-label">
            Body <span className="font-normal text-mist">(optional)</span>
          </label>
          <textarea
            id="n-body"
            className="field min-h-[100px] resize-y"
            value={values.body}
            onChange={(e) => update('body', e.target.value)}
            maxLength={4096}
            disabled={busy}
          />
          <div className="mt-1 flex justify-between text-xs text-mist">
            {errors.body && <span className="text-rose-600">{errors.body}</span>}
            <span className="ml-auto">{values.body.length}/4096</span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="n-priority" className="field-label">Priority</label>
            <select
              id="n-priority"
              className="field"
              value={values.priority}
              onChange={(e) => update('priority', e.target.value)}
              disabled={busy}
            >
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="n-expires" className="field-label">
              Expires at <span className="font-normal text-mist">(optional, UTC)</span>
            </label>
            <input
              id="n-expires"
              type="datetime-local"
              className="field"
              value={values.expiresAt}
              onChange={(e) => update('expiresAt', e.target.value)}
              disabled={busy}
            />
            {errors.expiresAt && <p className="mt-1 text-xs text-rose-600">{errors.expiresAt}</p>}
          </div>
        </div>

        <div>
          <label className="field-label">Target</label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {TARGET_TYPES.map((t) => {
              const active = values.targetType === t.value;
              return (
                <label
                  key={t.value}
                  className={
                    'flex cursor-pointer items-start gap-3 rounded-2xl border px-3 py-2.5 transition-colors ' +
                    (active ? 'border-accent bg-accent-soft' : 'border-line bg-white hover:bg-canvas')
                  }
                >
                  <input
                    type="radio"
                    name="n-target-type"
                    className="mt-1 h-4 w-4 accent-accent"
                    value={t.value}
                    checked={active}
                    onChange={() => update('targetType', t.value)}
                    disabled={busy}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-ink">{t.label}</span>
                    <span className="block text-[11px] leading-snug text-mist">{t.hint}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {targetMeta?.needs === 'role' && (
          <div>
            <label htmlFor="n-role" className="field-label">Role</label>
            <select
              id="n-role"
              className="field"
              value={values.targetValue}
              onChange={(e) => update('targetValue', e.target.value)}
              disabled={busy}
            >
              <option value="">— select role —</option>
              <option value="admin">admin</option>
              <option value="user">user</option>
            </select>
            {errors.targetValue && <p className="mt-1 text-xs text-rose-600">{errors.targetValue}</p>}
          </div>
        )}

        {targetMeta?.needs === 'user' && (
          <div>
            <label htmlFor="n-user" className="field-label">User</label>
            <select
              id="n-user"
              className="field"
              value={values.targetValue}
              onChange={(e) => update('targetValue', e.target.value)}
              disabled={busy}
            >
              <option value="">— select user —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName || u.login} (@{u.login}) · {u.role}
                </option>
              ))}
            </select>
            {errors.targetValue && <p className="mt-1 text-xs text-rose-600">{errors.targetValue}</p>}
          </div>
        )}
      </form>
    </Modal>
  );
}

function empty() {
  return {
    kind: 'announcement',
    priority: 'routine',
    title: '',
    body: '',
    targetType: 'all',
    targetValue: '',
    expiresAt: '',
  };
}
